import { oauthProviderClient } from '@better-auth/oauth-provider/client'
import { adminClient, emailOTPClient, inferAdditionalFields } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

import { ac, roles } from './access-control.ts'
import type { auth } from './server.ts'

/**
 * Browser-side auth client. Client components import this (or the named
 * re-exports below). Mirrors the server: same access-control catalogue and
 * roles, so `authClient.admin.hasPermission` agrees with the server guards.
 *
 * `baseURL` is intentionally unset — BetterAuth defaults to `/api/auth` on the
 * current origin, which is exactly where the app mounts its handler. Single
 * app, same origin, no CORS.
 *
 * `inferAdditionalFields<typeof auth>()` re-uses the server's `user`
 * additional-field declarations so the client session is typed with the FLOU
 * profile columns without restating them.
 *
 * `oauthProviderClient()` is what makes the MCP consent screen possible at all:
 * it is a FETCH plugin that lifts the raw, signed `window.location.search` into
 * the consent request body verbatim. The prompt is signed over its exact bytes,
 * so anything that re-serialises it — `URLSearchParams`, a router, a helpful
 * normalisation — invalidates the signature. Never rebuild that query by hand.
 */
export const authClient = createAuthClient({
  plugins: [
    adminClient({ ac, roles }),
    inferAdditionalFields<typeof auth>(),
    oauthProviderClient(),
    // `authClient.emailOtp.verifyEmail` / `.sendVerificationOtp` — the code the
    // registration form asks for. See `emailOTP` in `./server.ts` for why the
    // address is proved with a code rather than a link.
    emailOTPClient(),
  ],
})

export const { useSession, signIn, signOut, signUp, admin, requestPasswordReset, resetPassword } =
  authClient
